import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useCalendarStore, useCurrentuser } from '@uplift/contexts';
import { observer } from 'mobx-react-lite';
import { useAggregateAppointmentCounts } from '@uplift/queries';
import { CalendarViewTypes } from '@uplift/types';
import { useL10n } from '@uplift/hooks';
import { LoadableTypography, LoadableTypographyProps } from '@uplift/ui-v2';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: theme.spacing(5),
    background: 'red',
    backgroundColor: theme.palette.primary.background,
  },
}));


const CalculableValue: React.FC<{
  value: React.ReactNode;
  label: React.ReactNode;
  loading?: boolean;
  width?: LoadableTypographyProps['width'];
}> = ({ loading, value, label, width = '160px' }) => {
  return (
    <LoadableTypography loading={loading} width={width}>
      <strong>
        {value}
      </strong>
      &nbsp;
      {label}
    </LoadableTypography>
  );
};


export const CalendarMetrics = observer(() => {
  const { classes } = useStyles();
  const me = useCurrentuser();
  const calendarStore = useCalendarStore();
  const l10n = useL10n();
  const { patientCounts, refetch, fetching } = useAggregateAppointmentCounts(me?.id);

  React.useEffect(() => {
    refetch();
  }, [ calendarStore.appointments ]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFetching = calendarStore.isCalendarLoading || fetching;
  const appointments = React.useMemo(() => {
    if (isFetching) return [];
    return calendarStore.appointments ?? [];
  }, [ calendarStore.appointments, calendarStore.isCalendarLoading, fetching ]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSessionsBooked = React.useMemo(() => {
    return appointments.length;
  }, [ appointments ]);

  const newClientsCount = React.useMemo(() => {
    const firsts = appointments.filter(a => patientCounts[a.patient_id]?.[0] === a.id);

    return firsts.length;
  }, [ appointments, patientCounts ]);

  const clientsReturningCount = React.useMemo(() => {
    const notFirsts = appointments.filter(a => patientCounts[a.patient_id]?.[0] !== a.id);

    return notFirsts.length;
  }, [ appointments, patientCounts ]);

  const title = React.useMemo(() => {
    return {
      [CalendarViewTypes.timeGridDay]: l10n.calendar.metrics.title.daily,
      [CalendarViewTypes.timeGridWeek]: l10n.calendar.metrics.title.weekly,
      [CalendarViewTypes.dayGridMonth]: l10n.calendar.metrics.title.monthly,
    }[calendarStore?.calendarApi?.view?.type];
  }, [ calendarStore?.calendarApi?.view?.type, l10n ]);

  return (
    <Box className={classes.root}>
      <Box display="flex" gap="16px">
        <Typography>{title}:</Typography>
        <Box />
        <CalculableValue width="161.5px" loading={isFetching} value={totalSessionsBooked} label={l10n.calendar.metrics.items.totalSessionsBooked} />
        <Typography>∙</Typography>
        <CalculableValue width="147.5px" loading={isFetching} value={newClientsCount} label={l10n.calendar.metrics.items.newClientsBooked} />
        <Typography>∙</Typography>
        <CalculableValue width="122.3px" loading={isFetching} value={clientsReturningCount} label={l10n.calendar.metrics.items.clientsReturning} />
      </Box>
    </Box>
  );
});
